// Polish Phase 1 (AUDIO-07) — the single static take-two audio profile.
//
// WS0b (ARCA-87) THEME COLLAPSE: the board directive was "don't keep theming".
// The former per-theme audio profiles (Court / Emerald / Velvet / Midnight) are
// gone — there is one canonical Court-of-Runes sonic identity (organ + chime +
// paper rustle). The engine still SYNTHESIZES every cue and tints it via this
// profile (oscillator timbres, pitch transpose/register, a brightness multiplier
// on filter cutoffs, chorus detune, a feedback-delay "space" reverb send, and a
// noise-texture filter frequency) — there is simply no runtime swap now.

import type { ThemeKey } from '../theme/descriptor'
import { DEFAULT_THEME } from '../theme/descriptor'

export interface AudioThemeProfile {
  readonly key: ThemeKey
  /** Timbre for melodic/percussive cues (chime, bell, trace, coin). */
  readonly tonalType: OscillatorType
  /** Timbre for sustained chords (empower organ, queen, reveal stingers). */
  readonly organType: OscillatorType
  /** Global pitch offset in semitones — the court's register. */
  readonly transpose: number
  /** Multiplier on filter cutoffs — timbral brightness (>1 brighter/glassier). */
  readonly brightness: number
  /** Chorus detune in cents applied to layered voices for warmth. */
  readonly detune: number
  /** Reverb send amount [0,1] into the engine's feedback-delay space bus. */
  readonly spaceWet: number
  /** Space-bus delay time in seconds (longer = more cavernous). */
  readonly spaceDelay: number
  /** Space-bus feedback [0,1) (higher = longer tail). */
  readonly spaceFeedback: number
  /** Base filter frequency (Hz) for noise textures — paper/leaf/crackle/deep. */
  readonly textureFreq: number
}

/** The single canonical (Court of Runes) audio profile. */
export const DEFAULT_AUDIO_PROFILE: AudioThemeProfile = {
  key: DEFAULT_THEME,
  tonalType: 'triangle', // bright chime
  organType: 'sawtooth', // reedy court organ
  transpose: 0,
  brightness: 1,
  detune: 6,
  spaceWet: 0.18,
  spaceDelay: 0.13,
  spaceFeedback: 0.3,
  textureFreq: 4200, // dry paper rustle (high, papery)
}

/** Resolve the audio profile. With theming collapsed there is a single static
 * profile; the key argument is retained for call-site compatibility. */
export function audioProfileFor(
  _key?: ThemeKey | string | null | undefined,
): AudioThemeProfile {
  return DEFAULT_AUDIO_PROFILE
}
